import { useQuery } from '@tanstack/react-query';
import { authApiAuthed, dataCentralApi } from '@/api/client';
import { tokenStorage } from '@/lib/token-storage';
import { decodeJwtPayload } from '@/lib/jwt';
import type { User } from '@/auth/types';

export const authKeys = {
  currentUser: ['auth', 'current-user'] as const,
};

/** Raw user shape returned by the auth service. */
interface CurrentUser {
  user_uuid: string;
  user_id?: number;
  email: string;
  name?: string;
  role?: string;
  verified?: boolean;
  permissions?: string;
}

function toUser(c: CurrentUser): User {
  return {
    uuid: c.user_uuid,
    email: c.email,
    name: c.name,
    roles: c.role ? [c.role] : [],
  };
}

/** Decode the user from the access token payload (used for SSO/Keycloak sessions). */
function userFromToken(): User | null {
  const token = tokenStorage.access;
  if (!token) return null;
  try {
    const p = decodeJwtPayload(token) as {
      sub?: string;
      email?: string;
      preferred_username?: string;
      name?: string;
      given_name?: string;
      family_name?: string;
      realm_access?: { roles?: string[] };
    };
    return {
      uuid: p.sub ?? tokenStorage.uuid ?? '',
      email: p.email ?? p.preferred_username ?? '',
      name: p.name ?? ([p.given_name, p.family_name].filter(Boolean).join(' ') || undefined),
      roles: p.realm_access?.roles ?? [],
    };
  } catch {
    return null;
  }
}

/**
 * Resolve the base identity (uuid/email/name/roles). Mirrors the legacy @console
 * flow:
 *  - Keycloak sessions (SSO + standalone Google) → decode the access token
 *    (no server call; both carry Keycloak JWT claims).
 *  - Local sessions → `GET {AUTH_URL}/auth/v2/user_info` → `data.data.user`,
 *    falling back to the token if that call fails.
 */
async function resolveIdentity(): Promise<User> {
  if (tokenStorage.authMethod !== 'local') {
    const fromToken = userFromToken();
    if (fromToken) return fromToken;
    throw new Error('Could not read session.');
  }

  try {
    const res = await authApiAuthed.get('/auth/v2/user_info');
    const user = res.data?.data?.user as CurrentUser | undefined;
    if (user) return toUser(user);
  } catch (e) {
    const fromToken = userFromToken();
    if (fromToken) return fromToken;
    throw e;
  }
  const fromToken = userFromToken();
  if (fromToken) return fromToken;
  throw new Error('Could not load user profile.');
}

type MeResult =
  | { status: 'ok'; organizationId?: string } // live contact (org id may still be absent)
  | { status: 'no-contact' } // 200 but no contact — removed member, gate the session
  | { status: 'unavailable' }; // transient failure — do NOT gate on it

/**
 * Resolve the org context from `/partner/auth/me`. The org is NOT a token claim,
 * so it's read here — available even for brand-new accounts that own no services
 * yet (they still have a contact + customer_id). Distinguishes three outcomes so
 * the caller can gate a removed member without logging out on a transient blip.
 * `customer_id` may arrive as a plain number or a pgtype `{Int32, Valid}` shape.
 */
async function fetchMe(): Promise<MeResult> {
  try {
    const res = await dataCentralApi.get('/partner/auth/me');
    const contact = res.data?.result?.user?.contact;
    // A live user always carries a contact object; a removed member does not.
    if (!contact) return { status: 'no-contact' };
    const raw = contact.customer_id as
      | number
      | { Int32?: number; Valid?: boolean }
      | null
      | undefined;
    const id = typeof raw === 'object' && raw !== null ? raw.Int32 : raw;
    return { status: 'ok', organizationId: id ? String(id) : undefined };
  } catch {
    return { status: 'unavailable' };
  }
}

/**
 * Fetch the authenticated user: base identity + organization context, resolved in
 * parallel. A failed identity rejects (no session). A `no-contact` /me marks the
 * user `contactRevoked` (removed member → the app gates to an "access removed"
 * screen). A transient /me failure is non-fatal (leaves org context undefined).
 */
export async function fetchCurrentUser(): Promise<User> {
  const [user, me] = await Promise.all([resolveIdentity(), fetchMe()]);
  if (me.status === 'no-contact') return { ...user, contactRevoked: true };
  return me.status === 'ok' && me.organizationId
    ? { ...user, organizationId: me.organizationId }
    : user;
}

/**
 * Current-user query. `enabled` is driven by the AuthProvider's reactive token
 * state so it activates immediately after a session is established.
 */
export function useCurrentUser(enabled: boolean) {
  return useQuery({
    queryKey: authKeys.currentUser,
    queryFn: fetchCurrentUser,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
